import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { type AnalyticsResponse } from '../../services/api/analytics';
import { QUALITY_LABELS } from '../../utils/quality-labels';

interface Props {
  data: AnalyticsResponse | null;
  loading: boolean;
}

const qualityMeta: Record<
  'good' | 'degraded' | 'poor' | 'unanalyzed',
  { color: string; bg: string; text: string; Icon: typeof CheckCircle2 }
> = {
  good: {
    color: '#22c55e',
    bg: 'bg-emerald-500',
    text: 'text-emerald-700',
    Icon: CheckCircle2,
  },
  degraded: {
    color: '#f59e0b',
    bg: 'bg-amber-500',
    text: 'text-amber-700',
    Icon: AlertTriangle,
  },
  poor: {
    color: '#ef4444',
    bg: 'bg-rose-500',
    text: 'text-rose-700',
    Icon: XCircle,
  },
  unanalyzed: {
    color: '#94a3b8',
    bg: 'bg-slate-400',
    text: 'text-slate-600',
    Icon: HelpCircle,
  },
};

export const AnalyticsQualityCards = ({ data, loading }: Props) => {
  const totals = data?.quality?.totals;
  const sum = totals ? totals.good + totals.degraded + totals.poor + totals.unanalyzed : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Sessionskvalitet (från AI-analys)
        </h3>
        {!loading && sum > 0 && totals && (
          <span className="text-xs text-slate-500">
            {totals.good + totals.degraded + totals.poor} av {sum} analyserade
          </span>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(['good', 'degraded', 'poor', 'unanalyzed'] as const).map((key) => {
          const meta = qualityMeta[key];
          const count = totals?.[key] ?? 0;
          const pct = sum > 0 ? Math.round((count / sum) * 100) : 0;
          return (
            <div key={key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                <meta.Icon className="h-4 w-4" style={{ color: meta.color }} />
                {QUALITY_LABELS[key]}
              </div>
              <div className={`text-3xl font-bold ${meta.text}`}>
                {loading ? '\u2013' : count.toLocaleString('sv-SE')}
              </div>
              <div className="text-xs text-slate-500 mt-1">{pct}% av sessioner</div>
              <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${meta.bg}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {!loading && data?.quality?.daily && data.quality.daily.length > 0 && (
        <QualityTrendBars daily={data.quality.daily} />
      )}
    </div>
  );
};

const QualityTrendBars = ({ daily }: { daily: AnalyticsResponse['quality']['daily'] }) => {
  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('sv-SE', {
      month: 'short',
      day: 'numeric',
    });

  const maxTotal = Math.max(...daily.map((d) => d.good + d.degraded + d.poor + d.unanalyzed), 1);

  const n = daily.length;
  const labelIndices = new Set<number>();
  if (n > 0) {
    labelIndices.add(0);
    labelIndices.add(n - 1);
    const step = Math.max(1, Math.floor(n / 10));
    for (let i = step; i < n - 1; i += step) labelIndices.add(i);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
        Kvalitet per dag
      </div>
      <div className="flex h-40 items-end gap-1">
        {daily.map((d, i) => {
          const total = d.good + d.degraded + d.poor + d.unanalyzed;
          const heightPct = (total / maxTotal) * 100;
          return (
            <div
              key={d.date}
              className="flex flex-1 flex-col items-center gap-1"
              title={`${formatDate(d.date)} — god ${d.good} · försämrad ${d.degraded} · dålig ${d.poor} · ej analyserade ${d.unanalyzed}`}
            >
              <div
                className="flex w-full flex-col-reverse overflow-hidden rounded-sm"
                style={{ height: `${heightPct}%`, minHeight: total > 0 ? 2 : 0 }}
              >
                {d.good > 0 && <div className="bg-emerald-500" style={{ flexGrow: d.good }} />}
                {d.degraded > 0 && (
                  <div className="bg-amber-500" style={{ flexGrow: d.degraded }} />
                )}
                {d.poor > 0 && <div className="bg-rose-500" style={{ flexGrow: d.poor }} />}
                {d.unanalyzed > 0 && (
                  <div className="bg-slate-300" style={{ flexGrow: d.unanalyzed }} />
                )}
              </div>
              <div className="h-4 text-[10px] text-slate-400 leading-none">
                {labelIndices.has(i) ? formatDate(d.date) : ''}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
        <LegendDot color="bg-emerald-500" label={QUALITY_LABELS.good} />
        <LegendDot color="bg-amber-500" label={QUALITY_LABELS.degraded} />
        <LegendDot color="bg-rose-500" label={QUALITY_LABELS.poor} />
        <LegendDot color="bg-slate-300" label={QUALITY_LABELS.unanalyzed} />
      </div>
    </div>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-1.5">
    <div className={`h-2.5 w-2.5 rounded-sm ${color}`} />
    <span>{label}</span>
  </div>
);
